import { useMemo } from "react"

const ProfessionalDependencyArrow = ({ line, scrollTop = 0 }) => {
  if (!line) return null

  const { sourceX, sourceY, targetX, targetY, sourceIssue, targetIssue, linkType, isEpicLevel } = line

  // Adjust Y-coordinates for scroll position
  const adjustedSourceY = sourceY - scrollTop
  const adjustedTargetY = targetY - scrollTop

  // Don't render if both ends are completely outside the visible viewport
  const viewportBuffer = 200
  if (adjustedSourceY < -viewportBuffer && adjustedTargetY < -viewportBuffer) {
    return null
  }

  // Calculate control points for a smooth curve
  const dx = targetX - sourceX
  const controlOffset = Math.min(Math.max(Math.abs(dx) * 0.5, 30), 120)
  const controlPoint1X = sourceX + controlOffset
  const controlPoint1Y = adjustedSourceY
  const controlPoint2X = targetX - controlOffset
  const controlPoint2Y = adjustedTargetY

  // For epic-level dependencies, go all the way to target; for regular stop before arrow
  const arrowOffset = isEpicLevel ? 0 : 8
  const pathD = `
    M ${sourceX} ${adjustedSourceY}
    C ${controlPoint1X} ${controlPoint1Y},
      ${controlPoint2X} ${controlPoint2Y},
      ${targetX - arrowOffset} ${adjustedTargetY}
  `

  // Calculate midpoint for label
  const midX = sourceX + (targetX - sourceX) * 0.5
  const midY = adjustedSourceY + (adjustedTargetY - adjustedSourceY) * 0.5

  // Determine line color and style based on link type
  const getLineStyle = () => {
    if (isEpicLevel) {
      return {
        color: "#7b1fa2",
        dasharray: "6,3",
        label: "HAS DEPENDENCY",
        width: 3,
      }
    }

    const type = String(linkType).toLowerCase()
    switch (type) {
      case "blocks":
        return { color: "#d32f2f", dasharray: "4,3", label: "BLOCKS", width: 2 }
      case "is blocked by":
        return { color: "#ff9800", dasharray: "4,3", label: "BLOCKED BY", width: 2 }
      case "relates to":
        return { color: "#1976d2", dasharray: "6,2", label: "RELATES", width: 2 }
      default:
        return { color: "#666", dasharray: "4,3", label: String(linkType || "LINKS").toUpperCase(), width: 2 }
    }
  }

  const lineStyle = getLineStyle()

  return (
    <g className="dependency-line" style={{ pointerEvents: "none" }}>
      {/* Professional line with type-based styling */}
      <path
        d={pathD}
        stroke={lineStyle.color}
        strokeWidth={lineStyle.width}
        fill="none"
        strokeDasharray={lineStyle.dasharray}
        opacity={isEpicLevel ? 0.9 : 0.85}
        strokeLinecap="round"
        style={{ pointerEvents: "none" }}
      />

      {/* Arrowhead - only for non-epic level dependencies */}
      {!isEpicLevel && (
        <polygon
          points={`${targetX - 8},${adjustedTargetY - 4} ${targetX - 8},${adjustedTargetY + 4} ${targetX},${adjustedTargetY}`}
          fill={lineStyle.color}
          opacity="0.9"
          style={{ pointerEvents: "none" }}
        />
      )}

      {/* Connection points */}
      <circle cx={sourceX} cy={adjustedSourceY} r={isEpicLevel ? 4 : 3} fill={lineStyle.color} opacity={1} style={{ pointerEvents: "none" }} />
      <circle cx={targetX} cy={adjustedTargetY} r={isEpicLevel ? 4 : 2.5} fill={lineStyle.color} opacity={1} style={{ pointerEvents: "none" }} />

      {/* Label background */}
      <rect
        x={midX - 45}
        y={midY - 8}
        width="90"
        height="16"
        fill="white"
        stroke={lineStyle.color}
        strokeWidth="1"
        rx="3"
        opacity="0.95"
        style={{ pointerEvents: "none" }}
      />

      {/* Label text */}
      <text
        x={midX}
        y={midY + 4}
        textAnchor="middle"
        fontSize="9"
        fontWeight="bold"
        fill={lineStyle.color}
        style={{ pointerEvents: "none" }}
      >
        {lineStyle.label}
      </text>

      {/* Issue key labels - for epic level */}
      {isEpicLevel && (
        <>
          <text
            x={sourceX - 8}
            y={adjustedSourceY - 8}
            textAnchor="end"
            fontSize="9"
            fill={lineStyle.color}
            fontWeight="bold"
            style={{ pointerEvents: "none" }}
          >
            {sourceIssue}
          </text>

          <text
            x={targetX + 8}
            y={adjustedTargetY - 8}
            textAnchor="start"
            fontSize="9"
            fill={lineStyle.color}
            fontWeight="bold"
            style={{ pointerEvents: "none" }}
          >
            {targetIssue}
          </text>
        </>
      )}
    </g>
  )
}

const DependencyLines = ({
  dependencies = [],
  organizedIssues = [],
  expandedEpics = {},
  expandedStories = {},
  timeRange,
  timelineView,
  svgWidth = 0,
  taskDetailsPanelOpen,
  scrollTop = 0,
  calculateGanttPosition,
  sidebarOpen,
}) => {
  // Get visible issue list
  const visibleIssues = useMemo(() => {
    const issues = []

    organizedIssues.forEach((epic) => {
      issues.push(epic)

      const isEpicExpanded = Boolean(expandedEpics[epic.id] || expandedEpics[String(epic.id)])
      if (isEpicExpanded && epic.children) {
        epic.children.forEach((child) => {
          issues.push(child)

          const isStory = Number(child.issueType) === 3 || Number(child.issueType) === 4 || String(child.issueType).toLowerCase() === "story"
          const isStoryExpanded = Boolean(expandedStories[child.id] || expandedStories[String(child.id)])
          if (isStory && isStoryExpanded && child.children) {
            child.children.forEach((grandChild) => {
              issues.push(grandChild)
            })
          }
        })
      }
    })

    return issues
  }, [organizedIssues, expandedEpics, expandedStories])

  // Calculate dependency line with exact coordinates
  const calculateDependencyLine = (sourceIssue, targetIssue, sourcePosition, targetPosition, linkType = "blocks", isEpicLevel = false) => {
    if (!sourcePosition || !targetPosition) return null

    const sourceRowIndex = visibleIssues.findIndex((issue) => String(issue.id) === String(sourceIssue.id))
    const targetRowIndex = visibleIssues.findIndex((issue) => String(issue.id) === String(targetIssue.id))

    if (sourceRowIndex === -1 || targetRowIndex === -1) {
      return null
    }

    const rowHeight = 96
    const getBarCenterY = (rowIndex) => {
      return (rowIndex * rowHeight) + 48
    }

    const sourceY = getBarCenterY(sourceRowIndex)
    const targetY = getBarCenterY(targetRowIndex)

    // The SVG spans the entire Gantt timeline column (left: 400px to 100%)
    const ganttAreaWidth = (svgWidth && svgWidth > 50) ? svgWidth : 800

    const sourceLeftPercent = parseFloat(sourcePosition.left) || 0
    const sourceWidthPercent = parseFloat(sourcePosition.width) || 0
    const targetLeftPercent = parseFloat(targetPosition.left) || 0

    const sourceBarStartX = (sourceLeftPercent / 100) * ganttAreaWidth
    const sourceBarWidth = (sourceWidthPercent / 100) * ganttAreaWidth
    const targetBarStartX = (targetLeftPercent / 100) * ganttAreaWidth

    const sourceBarEndX = sourceBarStartX + sourceBarWidth

    return {
      sourceX: sourceBarEndX,
      sourceY,
      targetX: targetBarStartX,
      targetY,
      linkType: linkType,
      sourceIssue: sourceIssue.key,
      targetIssue: targetIssue.key,
      isEpicLevel: isEpicLevel,
    }
  }

  // Find epic parent for any issue
  const findEpicParent = (issue) => {
    if (!issue) return null
    const isEpic = Number(issue.issueType) === 2 || String(issue.issueType).toLowerCase() === "epic"
    if (isEpic) return issue

    const issueIdStr = String(issue.id)
    for (const epic of organizedIssues) {
      if (String(epic.id) === issueIdStr) return epic

      if (epic.children) {
        const directChild = epic.children.find((child) => String(child.id) === issueIdStr)
        if (directChild) return epic

        for (const child of epic.children) {
          if (child.children) {
            const grandChild = child.children.find((grand) => String(grand.id) === issueIdStr)
            if (grandChild) return epic
          }
        }
      }
    }
    return null
  }

  // Calculate epic-level dependency lines (when children have dependencies)
  const epicDependencyLines = useMemo(() => {
    const lines = []
    const processedEpicPairs = new Set()

    dependencies.forEach((dependency) => {
      const allFlatIssues = organizedIssues.flatMap((epic) => [
        epic,
        ...(epic.children || []),
        ...(epic.children?.flatMap((child) => child.children || []) || []),
      ])

      const sourceIssue = allFlatIssues.find((issue) => String(issue.id) === String(dependency.sourceIssueId))
      const targetIssue = allFlatIssues.find((issue) => String(issue.id) === String(dependency.targetIssueId))

      if (!sourceIssue || !targetIssue) return

      const sourceEpic = findEpicParent(sourceIssue)
      const targetEpic = findEpicParent(targetIssue)

      if (!sourceEpic || !targetEpic) return

      // Only create epic-level line if they belong to different epics
      if (String(sourceEpic.id) !== String(targetEpic.id)) {
        const epicPairKey = `${sourceEpic.id}-${targetEpic.id}`

        if (!processedEpicPairs.has(epicPairKey)) {
          processedEpicPairs.add(epicPairKey)

          // Check if both source and target children are hidden (epics not expanded)
          const isSourceChildHidden =
            String(sourceIssue.id) !== String(sourceEpic.id) &&
            !expandedEpics[sourceEpic.id] &&
            !expandedEpics[String(sourceEpic.id)]
          const isTargetChildHidden =
            String(targetIssue.id) !== String(targetEpic.id) &&
            !expandedEpics[targetEpic.id] &&
            !expandedEpics[String(targetEpic.id)]

          // Show epic-level dependency if the actual dependent children are not visible
          if (isSourceChildHidden || isTargetChildHidden) {
            const sourcePosition = calculateGanttPosition(sourceEpic)
            const targetPosition = calculateGanttPosition(targetEpic)

            if (sourcePosition && targetPosition) {
              const line = calculateDependencyLine(
                sourceEpic,
                targetEpic,
                sourcePosition,
                targetPosition,
                dependency.linkType || "has dependency",
                true
              )

              if (line) {
                lines.push({
                  ...line,
                  id: `epic-${epicPairKey}-${dependency.id || Math.random()}`,
                  dependency,
                })
              }
            }
          }
        }
      }
    })

    return lines
  }, [dependencies, organizedIssues, expandedEpics, calculateGanttPosition, svgWidth])

  // Calculate regular dependency lines (between visible issues)
  const regularDependencyLines = useMemo(() => {
    const lines = []

    dependencies.forEach((dependency) => {
      const sourceIssue = visibleIssues.find((issue) => String(issue.id) === String(dependency.sourceIssueId))
      const targetIssue = visibleIssues.find((issue) => String(issue.id) === String(dependency.targetIssueId))

      if (!sourceIssue || !targetIssue) return

      const sourcePosition = calculateGanttPosition(sourceIssue)
      const targetPosition = calculateGanttPosition(targetIssue)

      if (sourcePosition && targetPosition) {
        const line = calculateDependencyLine(
          sourceIssue,
          targetIssue,
          sourcePosition,
          targetPosition,
          dependency.linkType || "blocks",
          false
        )
        if (line) {
          lines.push({
            ...line,
            id: `reg-${dependency.sourceIssueId}-${dependency.targetIssueId}-${dependency.id || Math.random()}`,
            dependency,
          })
        }
      }
    })

    return lines
  }, [dependencies, visibleIssues, calculateGanttPosition, svgWidth])

  // Combine all lines
  const allDependencyLines = useMemo(() => {
    return [...epicDependencyLines, ...regularDependencyLines]
  }, [epicDependencyLines, regularDependencyLines])

  if (allDependencyLines.length === 0) {
    return null
  }

  return (
    <>
      {allDependencyLines.map((line) => (
        <ProfessionalDependencyArrow key={line.id} line={line} scrollTop={scrollTop} />
      ))}
    </>
  )
}

export default DependencyLines